import { type ActionData } from '../types/common';

export function scanDomForActions(): ActionData[] {
  if (typeof document === 'undefined') return [];

  // Selector matching standard buttons, anchors, custom clickable triggers
  const selector = [
    'button',
    'a',
    '[role="button"]',
    '[role="link"]',
    '[role="menuitem"]',
    'input[type="button"]',
    'input[type="submit"]',
    '.ant-btn',
    '.ant-menu-item',
    '.ant-dropdown-menu-item',
    '.clickable',
    '[class*="btn"]',
    '[class*="action"]',
  ].join(', ');

  const elements = Array.from(document.querySelectorAll(selector));
  const actions: ActionData[] = [];

  elements.forEach((el) => {
    const htmlEl = el as HTMLElement;

    // Filter out side panel & AI assistant controls to prevent recursive feedback
    if (
      htmlEl.closest('[class*="aiAssistant"]') || 
      htmlEl.closest('[data-testid="clear-chat"]') ||
      htmlEl.closest('[class*="sidePanel"]')
    ) {
      return;
    }

    // Filter out global navbar/headers/sidebar unless part of main content area
    if (htmlEl.closest('header') || htmlEl.closest('nav') || htmlEl.closest('.ant-layout-sider')) {
      if (!htmlEl.closest('main') && !htmlEl.closest('.ant-layout-content')) {
        return;
      }
    }

    // Capture visible text
    let label = htmlEl.textContent?.trim() || '';
    
    // Fallbacks for iconography triggers
    if (!label) {
      label = htmlEl.getAttribute('aria-label') || htmlEl.getAttribute('title') || htmlEl.getAttribute('alt') || '';
    }

    if (!label || label.length > 50) return;

    // Ignore known AI chat layout items
    const skipLabels = ['clear chat', 'ask geeky', 'bms tools', 'notepad'];
    if (skipLabels.includes(label.toLowerCase())) return;

    const id =
      htmlEl.getAttribute('data-testid') ||
      htmlEl.id ||
      label.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_');

    // Inspect multiple indicators to determine if disabled
    const isEnabled = !(
      htmlEl.hasAttribute('disabled') ||
      htmlEl.getAttribute('aria-disabled') === 'true' ||
      htmlEl.classList.contains('ant-btn-disabled') ||
      htmlEl.classList.contains('disabled') ||
      (htmlEl as any).disabled === true
    );

    if (!actions.some((act) => act.id === id)) {
      actions.push({
        id,
        label,
        isEnabled,
        description: htmlEl.getAttribute('title') || null,
      });
    }
  });

  return actions;
}

/**
 * Deduce current action status based on active router URL path
 */
export function getActionFromPath(pathname: string): string | null {
  if (pathname.includes('/edit')) return 'edit';
  if (pathname.includes('/create')) return 'create';
  if (pathname.includes('/cancel')) return 'cancel';
  if (pathname.includes('/reschedule')) return 'reschedule';
  return null;
}
